import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchModule,
  type ModuleFilters,
  type OrganizersEnvelope,
} from '../api/events';

/**
 * Stable query key for the Events → Organizers module envelope.
 * Exported so sibling code (e.g. an invalidator after an ingest) can
 * refer to the same key without re-deriving it.
 */
export function eventsOrganizersKey(filters?: ModuleFilters) {
  return ['events', 'organizers', filters ?? {}] as const;
}

/**
 * Fetch the Events → Organizers envelope (per-organizer rows plus
 * freshness / honesty metadata).
 *
 * Returns the full envelope so consumers can render `<Freshness/>`,
 * `<Denominator/>`, and `<AnomalyChip/>` alongside the leaderboard.
 *
 * Per data-quality rule #5, rows may carry `insufficient_data: true`
 * (events < MIN_EVENTS); ranking / aggregation lives in the view.
 */
export function useEventsOrganizers(
  filters?: ModuleFilters,
): UseQueryResult<OrganizersEnvelope, Error> {
  return useQuery({
    queryKey: eventsOrganizersKey(filters),
    queryFn: ({ signal }) => fetchModule('organizers', filters, { signal }),
    staleTime: 5 * 60 * 1000,
  });
}
